import { useState, type ReactNode } from "react";
import {
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useRequireAccess,
  useTranslate,
} from "ra-core";
import { Link, useNavigate, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { ListCardsSkeleton } from "../design/ListStates";
import { formatMxn } from "../quoter/PlanSchedule";
import type { Project } from "../types";
import { ExpenseFileField } from "./ExpenseFileField";
import { computeIvaAmount, EXPENSE_MESSAGES } from "./expenseRules";
import type { ExpenseErrors } from "./expenseRules";
import {
  buildExpensePayload,
  parseAmount,
  toExpenseFormValues,
  validateExpenseForm,
  type ExpenseFormValues,
} from "./expenseScreenRules";
import {
  EXPENSE_PAID_BY_VALUES,
  EXPENSE_PAYMENT_METHODS,
  type Expense,
  type ExpenseCategory,
} from "./expenseTypes";

const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-base disabled:opacity-60";

const Field = ({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-2">
    <Label htmlFor={id}>{label}</Label>
    {children}
    {error ? (
      <p id={`${id}-error`} className="text-sm text-destructive">
        {error}
      </p>
    ) : null}
  </div>
);

const SwitchRow = ({
  id,
  label,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <div className="flex min-h-11 items-center gap-3">
    <Switch
      id={id}
      checked={checked}
      disabled={disabled}
      onCheckedChange={onChange}
    />
    <Label htmlFor={id}>{label}</Label>
  </div>
);

/** Active categories (never archived ones), plus the expense's own when it is archived. */
const useCategoryChoices = (expense?: Expense) => {
  const { data } = useGetList<ExpenseCategory>("expense_categories", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "name", order: "ASC" },
    filter: { "archived_at@is": null },
  });
  const choices = (data ?? []).map((category) => ({
    id: String(category.id),
    name: category.name,
  }));
  if (expense && !choices.some((c) => c.id === String(expense.category_id))) {
    choices.push({
      id: String(expense.category_id),
      name: expense.category_name,
    });
  }
  return choices;
};

/** Non-archived projects, plus the expense's own when it was archived since. */
const useProjectChoices = (expense?: Expense) => {
  const { data } = useGetList<Project>("projects", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "name", order: "ASC" },
    filter: { archived: false },
  });
  const choices = (data ?? []).map((project) => ({
    id: String(project.id),
    name: project.name,
  }));
  if (
    expense?.project_id != null &&
    !choices.some((c) => c.id === String(expense.project_id))
  ) {
    choices.push({
      id: String(expense.project_id),
      name: expense.project_name ?? String(expense.project_id),
    });
  }
  return choices;
};

type ExpenseFormProps = {
  expense?: Expense;
  cancelTo: string;
  onSaved: (id: Expense["id"]) => void;
};

/**
 * Create/edit form of an expense. Values are validated with the same rules the
 * data layer enforces (`validateExpense`) and only the editable columns are
 * sent (`toExpenseWriteData`, through the provider callbacks). Files upload to
 * the private bucket as soon as they are picked.
 */
export const ExpenseForm = ({
  expense,
  cancelTo,
  onSaved,
}: ExpenseFormProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const categories = useCategoryChoices(expense);
  const projects = useProjectChoices(expense);
  const [values, setValues] = useState<ExpenseFormValues>(
    toExpenseFormValues(expense),
  );
  const [errors, setErrors] = useState<ExpenseErrors>({});
  const [saveError, setSaveError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const idPrefix = `expense-${expense?.id ?? "new"}`;

  const set = <Key extends keyof ExpenseFormValues>(
    key: Key,
    value: ExpenseFormValues[Key],
  ) => setValues((current) => ({ ...current, [key]: value }));

  const amount = parseAmount(values.amount);
  const ivaAmount =
    Number.isFinite(amount) && amount > 0
      ? computeIvaAmount(amount, values.has_iva, values.iva_rate)
      : 0;

  const handleSave = async () => {
    const found = validateExpenseForm(values);
    setErrors(found);
    setSaveError(undefined);
    if (Object.keys(found).length > 0) return;
    setIsSaving(true);
    try {
      const data = buildExpensePayload(values);
      if (expense) {
        await dataProvider.update("expenses", {
          id: expense.id,
          data,
          previousData: expense,
        });
        notify("crm.expenses.updated_notice");
        onSaved(expense.id);
      } else {
        const created = await dataProvider.create<Expense>("expenses", {
          data,
        });
        notify("crm.expenses.created_notice");
        onSaved(created.data.id);
      }
    } catch (error) {
      setSaveError(
        error instanceof Error && error.message
          ? error.message
          : translate("crm.expenses.save_error"),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void handleSave();
      }}
    >
      <Field
        id={`${idPrefix}-date`}
        label={translate("crm.expenses.fields.date")}
        error={errors.expense_date}
      >
        <Input
          id={`${idPrefix}-date`}
          type="date"
          value={values.expense_date}
          disabled={isSaving}
          aria-invalid={errors.expense_date != null}
          onChange={(event) => set("expense_date", event.target.value)}
        />
      </Field>
      <Field
        id={`${idPrefix}-concept`}
        label={translate("crm.expenses.fields.concept")}
        error={errors.concept}
      >
        <Input
          id={`${idPrefix}-concept`}
          value={values.concept}
          disabled={isSaving}
          aria-invalid={errors.concept != null}
          onChange={(event) => set("concept", event.target.value)}
        />
      </Field>
      <Field
        id={`${idPrefix}-category`}
        label={translate("crm.expenses.fields.category")}
        error={errors.category_id}
      >
        <select
          id={`${idPrefix}-category`}
          className={SELECT_CLASS}
          value={values.category_id}
          disabled={isSaving}
          aria-invalid={errors.category_id != null}
          onChange={(event) => set("category_id", event.target.value)}
        >
          <option value="">{translate("crm.expenses.choose_category")}</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </Field>
      <Field
        id={`${idPrefix}-supplier`}
        label={translate("crm.expenses.fields.supplier")}
      >
        <Input
          id={`${idPrefix}-supplier`}
          value={values.supplier}
          disabled={isSaving}
          onChange={(event) => set("supplier", event.target.value)}
        />
      </Field>
      <Field
        id={`${idPrefix}-amount`}
        label={translate("crm.expenses.fields.amount")}
        error={errors.amount_mxn}
      >
        <Input
          id={`${idPrefix}-amount`}
          type="number"
          min={0}
          step="0.01"
          inputMode="decimal"
          className="tabular-nums"
          value={values.amount}
          disabled={isSaving}
          aria-invalid={errors.amount_mxn != null}
          onChange={(event) => set("amount", event.target.value)}
        />
      </Field>

      <SwitchRow
        id={`${idPrefix}-has-iva`}
        label={translate("crm.expenses.fields.has_iva")}
        checked={values.has_iva}
        disabled={isSaving}
        onChange={(checked) => set("has_iva", checked)}
      />
      {values.has_iva ? (
        <p data-testid="expense-iva-preview" className="tabular-nums text-sm">
          {translate("crm.expenses.iva_preview", {
            amount: formatMxn(ivaAmount),
            pct: Math.round(values.iva_rate * 10000) / 100,
          })}
        </p>
      ) : null}

      <SwitchRow
        id={`${idPrefix}-invoiced`}
        label={translate("crm.expenses.fields.invoiced_switch")}
        checked={values.is_invoiced}
        disabled={isSaving}
        onChange={(checked) => set("is_invoiced", checked)}
      />
      {values.is_invoiced ? (
        <fieldset
          data-testid="expense-cfdi-block"
          className="flex flex-col gap-4 rounded-md border p-4"
        >
          <Field
            id={`${idPrefix}-cfdi-uuid`}
            label={translate("crm.expenses.fields.cfdi_uuid")}
            error={errors.cfdi_uuid}
          >
            <Input
              id={`${idPrefix}-cfdi-uuid`}
              className="tabular-nums"
              value={values.cfdi_uuid}
              disabled={isSaving}
              aria-invalid={errors.cfdi_uuid != null}
              onChange={(event) => set("cfdi_uuid", event.target.value)}
            />
          </Field>
          <ExpenseFileField
            id={`${idPrefix}-cfdi-pdf`}
            kind="cfdi_pdf"
            label={translate("crm.expenses.fields.cfdi_pdf")}
            path={values.cfdi_pdf_path}
            disabled={isSaving}
            onChange={(path) => set("cfdi_pdf_path", path)}
          />
          <ExpenseFileField
            id={`${idPrefix}-cfdi-xml`}
            kind="cfdi_xml"
            label={translate("crm.expenses.fields.cfdi_xml")}
            path={values.cfdi_xml_path}
            disabled={isSaving}
            onChange={(path) => set("cfdi_xml_path", path)}
          />
        </fieldset>
      ) : null}

      <Field
        id={`${idPrefix}-paid-by`}
        label={translate("crm.expenses.fields.paid_by")}
        error={errors.paid_by}
      >
        <select
          id={`${idPrefix}-paid-by`}
          className={SELECT_CLASS}
          value={values.paid_by}
          disabled={isSaving}
          onChange={(event) =>
            set("paid_by", event.target.value as ExpenseFormValues["paid_by"])
          }
        >
          {EXPENSE_PAID_BY_VALUES.map((value) => (
            <option key={value} value={value}>
              {translate(`crm.expenses.paid_by_values.${value}`)}
            </option>
          ))}
        </select>
      </Field>
      {values.paid_by === "otro" ? (
        <Field
          id={`${idPrefix}-paid-by-other`}
          label={translate("crm.expenses.fields.paid_by_other")}
          error={errors.paid_by_other}
        >
          <Input
            id={`${idPrefix}-paid-by-other`}
            value={values.paid_by_other}
            disabled={isSaving}
            aria-invalid={errors.paid_by_other != null}
            onChange={(event) => set("paid_by_other", event.target.value)}
          />
        </Field>
      ) : null}
      <Field
        id={`${idPrefix}-method`}
        label={translate("crm.expenses.fields.payment_method")}
        error={errors.payment_method}
      >
        <select
          id={`${idPrefix}-method`}
          className={SELECT_CLASS}
          value={values.payment_method}
          disabled={isSaving}
          onChange={(event) =>
            set(
              "payment_method",
              event.target.value as ExpenseFormValues["payment_method"],
            )
          }
        >
          {EXPENSE_PAYMENT_METHODS.map((value) => (
            <option key={value} value={value}>
              {translate(`crm.expenses.payment_methods.${value}`)}
            </option>
          ))}
        </select>
      </Field>
      <Field
        id={`${idPrefix}-project`}
        label={translate("crm.expenses.fields.project")}
      >
        <select
          id={`${idPrefix}-project`}
          className={SELECT_CLASS}
          value={values.project_id}
          disabled={isSaving}
          onChange={(event) => set("project_id", event.target.value)}
        >
          <option value="">{translate("crm.expenses.no_project")}</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
      </Field>
      <Field
        id={`${idPrefix}-notes`}
        label={translate("crm.expenses.fields.notes")}
      >
        <Textarea
          id={`${idPrefix}-notes`}
          value={values.notes}
          disabled={isSaving}
          onChange={(event) => set("notes", event.target.value)}
        />
      </Field>
      <ExpenseFileField
        id={`${idPrefix}-receipt`}
        kind="receipt"
        label={translate("crm.expenses.fields.receipt")}
        path={values.receipt_path}
        disabled={isSaving}
        onChange={(path) => set("receipt_path", path)}
      />

      {saveError ? (
        <p role="alert" className="text-sm text-destructive">
          {saveError}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button asChild variant="ghost" className="min-h-11">
          <Link to={cancelTo}>{translate("ra.action.cancel")}</Link>
        </Button>
        {/* design-system.md section 6: the single primary action of the form. */}
        <Button
          type="submit"
          variant="gradient"
          className="min-h-11"
          disabled={isSaving}
        >
          {translate("ra.action.save")}
        </Button>
      </div>
    </form>
  );
};

const FormPage = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className="mx-auto max-w-2xl space-y-4 p-4 pb-8">
    <h1 className="text-2xl font-semibold">{title}</h1>
    <Card>
      <CardContent>{children}</CardContent>
    </Card>
  </div>
);

/** "Nuevo gasto" page. */
export const ExpenseCreate = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  // Redirects to the standard access-denied page for a role without access.
  const { isPending } = useRequireAccess({
    resource: "expenses",
    action: "create",
  });
  return (
    <FormPage title={translate("crm.expenses.new")}>
      {isPending ? (
        <ListCardsSkeleton rows={3} />
      ) : (
        <ExpenseForm
          cancelTo="/expenses"
          onSaved={(id) => navigate(`/expenses/${id}/show`)}
        />
      )}
    </FormPage>
  );
};

/** "Editar gasto" page: a cancelled expense is read-only and cannot be edited. */
export const ExpenseEdit = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { id } = useParams();
  const { isPending: isCheckingAccess } = useRequireAccess({
    resource: "expenses",
    action: "edit",
  });
  const { data: expense, isPending } = useGetOne<Expense>(
    "expenses",
    { id: id as string },
    { enabled: id != null && !isCheckingAccess },
  );
  return (
    <FormPage title={translate("crm.expenses.edit_title")}>
      {isCheckingAccess || isPending || !expense ? (
        <ListCardsSkeleton rows={3} />
      ) : expense.cancelled_at ? (
        <div className="flex flex-col gap-3">
          <p role="alert" className="text-sm text-destructive">
            {EXPENSE_MESSAGES.cancelled}
          </p>
          <Button asChild variant="outline" className="min-h-11 self-start">
            <Link to={`/expenses/${expense.id}/show`}>
              {translate("crm.expenses.back_to_detail")}
            </Link>
          </Button>
        </div>
      ) : (
        <ExpenseForm
          expense={expense}
          cancelTo={`/expenses/${expense.id}/show`}
          onSaved={(savedId) => navigate(`/expenses/${savedId}/show`)}
        />
      )}
    </FormPage>
  );
};
